import { useContext, useEffect, useState } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { Dialog, Stack, TextField } from '@fluentui/react';
import { CopyRegular } from '@fluentui/react-icons';

import Chat from '../chat/Chat'; // Importing Chat

import { CosmosDBStatus } from '../../api';
import Contoso from '../../assets/Contoso.svg';
import { HistoryButton, ShareButton } from '../../components/common/Button';
import { AppStateContext } from '../../state/AppProvider';
//import { fetchDocumentNames/*, verifySearchIndex*/ } from '../../api/azureSearch'; // Import fetchDocumentNames function
// import { fetchDocumentNames/*, verifySearchIndex*/ } from '../../api/api'; // Disabled: no longer fetching documents from /api/search


import styles from './Layout.module.css';

const Layout = () => {
    const [isSharePanelOpen, setIsSharePanelOpen] = useState<boolean>(false);
    const [copyClicked, setCopyClicked] = useState<boolean>(false);
    const [copyText, setCopyText] = useState<string>('Copy URL');
    const [shareLabel, setShareLabel] = useState<string | undefined>('Share');
    const [hideHistoryLabel, setHideHistoryLabel] = useState<string>('Hide chat history');
    const [showHistoryLabel, setShowHistoryLabel] = useState<string>('Show chat history');
    const [logo, setLogo] = useState('');
    const [documents, setDocuments] = useState<string[]>([]); // State for documents
    const [selectedDocument, setSelectedDocument] = useState<string | null>(null); // State for selected document
    const appStateContext = useContext(AppStateContext);
    const ui = appStateContext?.state.frontendSettings?.ui;

    const handleShareClick = () => {
        setIsSharePanelOpen(true);
    };

    const handleSharePanelDismiss = () => {
        setIsSharePanelOpen(false);
        setCopyClicked(false);
        setCopyText('Copy URL');
    };

    const handleCopyClick = () => {
        navigator.clipboard.writeText(window.location.href);
        setCopyClicked(true);
    };

    const handleHistoryClick = () => {
        appStateContext?.dispatch({ type: 'TOGGLE_CHAT_HISTORY' });
    };

    // In Layout.tsx, add to useEffect
    /*useEffect(() => {
        const init = async () => {
            try {
                //await verifySearchIndex();
                const docs = await fetchDocumentNames();
                setDocuments(docs);
            } catch (error) {
                console.error('Failed to initialize:', error);
            }
        };
        init();
    }, []);*/


    useEffect(() => {
        if (!appStateContext?.state.isLoading) {
            setLogo(ui?.logo || Contoso);
        }
    }, [appStateContext?.state.isLoading]);

    useEffect(() => {
        if (copyClicked) {
            setCopyText('Copied URL');
        }
    }, [copyClicked]);

    // Fetch document names from Azure Cognitive Search
    // Disabled: Document selector now uses a static list; do not call /api/search.
    /*
    useEffect(() => {
        const loadDocuments = async () => {
            try {
                const docs = await fetchDocumentNames(selectedDocument ?? undefined);
                        
                // Deduplicate document list before setting state
                const uniqueDocs = Array.from(new Set(docs.map(doc => doc.trim())));
                        
                setDocuments(uniqueDocs);
            } catch (error) {
                console.error('Failed to fetch documents:', error);
            }
        };
        loadDocuments();
    }, [selectedDocument]);
    */

    useEffect(() => {
        //console.log("Updated documents state:", documents);
    }, [documents]);

    // Handle dropdown changes
    const handleDocumentChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedDocument(event.target.value);
        
    };

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 480) {
                setShareLabel(undefined);
                setHideHistoryLabel('Hide history');
                setShowHistoryLabel('Show history');
            } else {
                setShareLabel('Share');
                setHideHistoryLabel('Hide chat history');
                setShowHistoryLabel('Show chat history');
            }
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <div className={styles.layout}>
            <header className={styles.header} role={'banner'}>
                <Stack horizontal verticalAlign="center" horizontalAlign="space-between">
                    <Stack horizontal verticalAlign="center">
                        
                        <Link to="/" className={styles.headerTitleContainer}>
                            <h1 className={styles.headerTitle}>{ui?.title}</h1>
                        </Link>
                    </Stack>
                    <Stack horizontal tokens={{ childrenGap: 4 }} className={styles.shareButtonContainer}>
                        {appStateContext?.state.isCosmosDBAvailable?.status !== CosmosDBStatus.NotConfigured && ui?.show_chat_history_button !== false && (
                            <HistoryButton
                                onClick={handleHistoryClick}
                                text={appStateContext?.state?.isChatHistoryOpen ? hideHistoryLabel : showHistoryLabel}
                            />
                        )}
                      
                        {ui?.show_share_button && <ShareButton onClick={handleShareClick} text={shareLabel} />}
                    </Stack>
                </Stack>
            </header>
            
            <Outlet context={{ selectedDocument, documents }} />
            
            <Dialog
                onDismiss={handleSharePanelDismiss}
                hidden={!isSharePanelOpen}
                styles={{
                    main: [
                        {
                            selectors: {
                                ['@media (min-width: 480px)']: {
                                    maxWidth: '600px',
                                    background: '#FFFFFF',
                                    boxShadow: '0px 14px 28.8px rgba(0, 0, 0, 0.24), 0px 0px 8px rgba(0, 0, 0, 0.2)',
                                    borderRadius: '8px',
                                    maxHeight: '200px',
                                    minHeight: '100px',
                                },
                            },
                        },
                    ],
                }}
                dialogContentProps={{
                    title: 'Share the web app',
                    showCloseButton: true,
                }}
            >
                <Stack horizontal verticalAlign="center" style={{ gap: '8px' }}>
                    <TextField className={styles.urlTextBox} defaultValue={window.location.href} readOnly />
                    <div
                        className={styles.copyButtonContainer}
                        role="button"
                        tabIndex={0}
                        aria-label="Copy"
                        onClick={handleCopyClick}
                        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ' ? handleCopyClick() : null)}
                    >
                        <CopyRegular className={styles.copyButton} />
                        <span className={styles.copyButtonText}>{copyText}</span>
                    </div>
                </Stack>
            </Dialog>
        </div>
    );
};

export default Layout;
